import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import './Finance.scss'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Users/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Users/export.svg'
import arrowLeft from 'BoostXpressAdmin/Assets/Icon/Users/arrow-left.png'
import arrowRight from 'BoostXpressAdmin/Assets/Icon/Users/arrow-right.svg'
import { orderBXService } from 'BoostXpressAdmin/Services/orderBXService'
import { OrderProduct, OrderStatus, PaymentStatus } from 'BoostXpressAdmin/Model/orderXmodel'
import fillingStationsService, {
  FillingStation,
} from 'BoostXpressAdmin/Services/fillingStationsService'
import { InvestorTokenAssetStatus } from 'Services/investorTokenAssetsService'
import orderService from 'Services/orderService'
import customerService from 'Services/customerService'
import { dashboardBXService } from 'BoostXpressAdmin/Services/dashboardBXService'
import { ReportStatus } from 'BoostXpressAdmin/Model/cashReconciliationXModel'

type FinanceCategory = 'boostxpress' | 'stockManagement' | 'tdla' | 'subscriptionOrders'

type FinanceSubTabMap = {
  boostxpress: 'userOrders' | 'posTerminals' | 'transactions' | 'cashReconciliation'
  stockManagement: 'overview'
  tdla: 'investments' | 'tradersCard'
  subscriptionOrders: 'tgPlus' | 'orders'
}

type FinanceSubTab = FinanceSubTabMap[FinanceCategory]

type FinanceMetric = {
  label: string
  value: string
  helper: string
  tone: 'blue' | 'green' | 'purple'
}

type FinanceStatus = string

import BoostXpressOrdersTable from './Components/BoostXpressOrdersTable'
import PosTerminalsTable from './Components/PosTerminalsTable'
import StockManagementTable from './Components/StockManagementTable'
import TdlaInvestmentsTable from './Components/TdlaInvestmentsTable'
import TradersCardTable from './Components/TradersCardTable'
import SubscriptionOrdersTable from './Components/SubscriptionOrdersTable'
import CashReconciliationTable from './Components/CashReconciliationTable'

export type FinanceRow = {
  id: number | string
  index?: number
  station?: string
  volumeSoldToday?: string
  volumeLeft?: string
  volumeLeftTone?: 'green' | 'orange'
  amountSoldToday?: string
  user?: string
  investor?: string
  phone?: string
  reference?: string
  amount?: string
  type?: string
  subscription?: string
  manager?: string
  reportDate?: string
  totalSales?: string
  variance?: string
  date?: string
  expiryDate?: string
  pool?: string
  status: FinanceStatus
  statusLabel: string
  uiStatus?: string
  action?: string
}

export type FinanceColumn = {
  key: string
  label: string
  className?: string
}

const PAGE_SIZE = 10

const categoryTabs: { key: FinanceCategory; label: string }[] = [
  { key: 'boostxpress', label: 'BoostXpress' },
  { key: 'stockManagement', label: 'Stock Management' },
  { key: 'tdla', label: 'TDLA' },
  { key: 'subscriptionOrders', label: 'Solar system' },
]

const subTabLabels: Record<
  Exclude<FinanceCategory, 'stockManagement'>,
  { key: string; label: string }[]
> = {
  boostxpress: [
    { key: 'userOrders', label: 'User Orders' },
    { key: 'posTerminals', label: 'POS Terminals' },
    { key: 'transactions', label: 'Transactions' },
    { key: 'cashReconciliation', label: 'Station Report' },
  ],
  tdla: [
    { key: 'investments', label: 'Investments' },
    { key: 'tradersCard', label: 'TradersCard' },
  ],
  subscriptionOrders: [
    { key: 'tgPlus', label: 'TG Plus' },
    { key: 'orders', label: 'TG Homes' },
  ],
}

const defaultSubTabs: FinanceSubTabMap = {
  boostxpress: 'userOrders',
  stockManagement: 'overview',
  tdla: 'investments',
  subscriptionOrders: 'tgPlus',
}

const exportFileNames: Record<FinanceSubTab, string> = {
  userOrders: 'user-orders',
  posTerminals: 'pos-terminals',
  transactions: 'transactions',
  cashReconciliation: 'station-reports',
  overview: 'stock',
  investments: 'investments',
  tradersCard: 'traders-card',
  tgPlus: 'tg-plus',
  orders: 'tg-homes',
}

const statusFilterOptionsByCategory: Record<FinanceCategory, { value: string; label: string }[]> = {
  boostxpress: [
    { value: '', label: 'All Statuses' },
    { value: 'CREDIT', label: 'Credit' },
    { value: 'DEBIT', label: 'Debit' },
    { value: 'ACTIVE', label: 'Active' },
  ],
  stockManagement: [
    { value: '', label: 'All Statuses' },
    { value: 'OPTIMAL', label: 'Optimal' },
    { value: 'LOW', label: 'Low' },
  ],
  tdla: [
    { value: '', label: 'All Statuses' },
    { value: 'ACTIVE', label: 'Active' },
  ],
  subscriptionOrders: [
    { value: '', label: 'All Statuses' },
    { value: 'ACTIVE', label: 'Active' },
  ],
}

const tdlaStatusFilterOptions: { value: InvestorTokenAssetStatus; label: string }[] = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'LIQUIDATED', label: 'Liquidated' },
]

const tradersCardStatusFilterOptions = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
]

const Finance = () => {
  const [activeCategory, setActiveCategory] = useState<FinanceCategory>('stockManagement')
  const [activeSubTab, setActiveSubTab] = useState<FinanceSubTab>('overview')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  const [bxOrderStatus, setBxOrderStatus] = useState<OrderStatus | ''>('')
  const [bxPaymentStatus, setBxPaymentStatus] = useState<PaymentStatus | ''>('')
  const [bxProduct, setBxProduct] = useState<OrderProduct | ''>('')
  const [bxUserResourceId, setBxUserResourceId] = useState('')
  const [bxStationId, setBxStationId] = useState<string | undefined>(undefined)
  const [bxStartDate, setBxStartDate] = useState('')
  const [bxEndDate, setBxEndDate] = useState('')
  const [bxStations, setBxStations] = useState<FillingStation[]>([])

  const [bxCashReconStatus, setBxCashReconStatus] = useState<ReportStatus | 'ALL' | ''>('')

  const [bxPosFrom, setBxPosFrom] = useState<string | undefined>()
  const [bxPosTo, setBxPosTo] = useState<string | undefined>()
  const [bxPosTerminalId, setBxPosTerminalId] = useState<string | undefined>()
  const [bxPosProvider, setBxPosProvider] = useState<string | undefined>()

  const [tdlaStatus, setTdlaStatus] = useState<InvestorTokenAssetStatus>('ACTIVE')
  const [tradersCardStatus, setTradersCardStatus] = useState<string>('ALL')

  const [regularOrderCount, setRegularOrderCount] = useState(0)
  const [bxOrderCount, setBxOrderCount] = useState(0)

  const [tgUserCount, setTgUserCount] = useState(0)
  const [bxUserCount, setBxUserCount] = useState(0)

  const financeMetrics: FinanceMetric[] = useMemo(
    () => [
      {
        label: 'Total TG Orders',
        value: regularOrderCount.toLocaleString(),
        helper: '↑ 22% increase',
        tone: 'blue',
      },
      {
        label: 'Total BoostXpress Orders',
        value: bxOrderCount.toLocaleString(),
        helper: '↑ 22% increase',
        tone: 'blue',
      },
      { label: 'Total Funds', value: '₦0.0', helper: 'Monthly throughput', tone: 'green' },
      {
        label: 'Total TG Users',
        value: tgUserCount.toLocaleString(),
        helper: '+425 this month',
        tone: 'purple',
      },
      {
        label: 'Total BoostXpress Users',
        value: bxUserCount.toLocaleString(),
        helper: '+425 this month',
        tone: 'purple',
      },
    ],
    [regularOrderCount, bxOrderCount, tgUserCount, bxUserCount],
  )

  const secondaryTabsRef = useRef<HTMLDivElement | null>(null)
  const tableSectionRef = useRef<HTMLDivElement | null>(null)
  const secondaryTabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const [secondaryIndicator, setSecondaryIndicator] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  })

  const resolvedSubTab = useMemo(() => {
    return defaultSubTabs[activeCategory] === activeSubTab ||
      subTabLabels[activeCategory as keyof typeof subTabLabels]?.some((t) => t.key === activeSubTab)
      ? activeSubTab
      : defaultSubTabs[activeCategory]
  }, [activeCategory, activeSubTab])

  const isBoostXpressOrdersView =
    activeCategory === 'boostxpress' && resolvedSubTab === 'userOrders'
  const isBoostXpressPosView = activeCategory === 'boostxpress' && resolvedSubTab === 'posTerminals'
  const isStockManagementView = activeCategory === 'stockManagement'
  const isTdlaInvestmentsView = activeCategory === 'tdla' && resolvedSubTab === 'investments'
  const isTdlaTradersCardView = activeCategory === 'tdla' && resolvedSubTab === 'tradersCard'
  const isTgPlusView = activeCategory === 'subscriptionOrders' && resolvedSubTab === 'tgPlus'
  const isTransactionsView = activeCategory === 'boostxpress' && resolvedSubTab === 'transactions'
  const isCashReconciliationView =
    activeCategory === 'boostxpress' && resolvedSubTab === 'cashReconciliation'

  const activeSearchPlaceholder = useMemo(() => {
    if (activeCategory === 'boostxpress') {
      if (resolvedSubTab === 'userOrders') return 'Search by user, phone or reference...'
      if (resolvedSubTab === 'posTerminals') return 'Search by Terminal ID or Phone...'
      if (resolvedSubTab === 'transactions') return 'Search by user, phone or reference...'
      if (resolvedSubTab === 'cashReconciliation') return 'Search by station or manager...'
    }
    if (activeCategory === 'stockManagement') return 'Search by station name...'
    if (activeCategory === 'tdla') return 'Search by investor or reference'
    return 'Search...'
  }, [activeCategory, resolvedSubTab])

  useEffect(() => {
    setActiveSubTab(defaultSubTabs[activeCategory])
    setSearchTerm('')
    setStatusFilter('')
    setShowFilters(false)
    setPageNumber(1)
    setBxOrderStatus('')
    setBxPaymentStatus('')
    setBxProduct('')
    setBxUserResourceId('')
    setBxStationId(undefined)
    setBxStartDate('')
    setBxEndDate('')
    setBxPosFrom(undefined)
    setBxPosTo(undefined)
    setBxPosTerminalId(undefined)
    setBxPosProvider(undefined)
    setBxCashReconStatus('')
  }, [activeCategory])

  useEffect(() => {
    setSearchTerm('')
    setStatusFilter('')
    setShowFilters(false)
    setPageNumber(1)
    setBxOrderStatus('')
    setBxPaymentStatus('')
    setBxProduct('')
    setBxUserResourceId('')
    setBxStationId(undefined)
    setBxStartDate('')
    setBxEndDate('')
    setBxPosFrom(undefined)
    setBxPosTo(undefined)
    setBxPosTerminalId(undefined)
    setBxPosProvider(undefined)
    setBxCashReconStatus('')
  }, [activeSubTab])

  useEffect(() => {
    if (activeSubTab !== resolvedSubTab) {
      setActiveSubTab(resolvedSubTab)
    }
  }, [activeSubTab, resolvedSubTab])

  useLayoutEffect(() => {
    if (activeCategory === 'stockManagement') return

    const container = secondaryTabsRef.current
    const activeEl = secondaryTabRefs.current[resolvedSubTab]
    if (!container || !activeEl) return

    const containerRect = container.getBoundingClientRect()
    const activeRect = activeEl.getBoundingClientRect()
    const left = activeRect.left - containerRect.left
    const width = activeRect.width

    setSecondaryIndicator({ left, width })
  }, [activeCategory, resolvedSubTab])

  useEffect(() => {
    if (!isBoostXpressOrdersView) return

    let cancelled = false

    const loadStations = async () => {
      try {
        const res = await fillingStationsService.list({
          pageNumber: 1,
          sortOrder: 'DESC',
        })
        if (cancelled) return
        setBxStations(res.data?.dataset ?? [])
      } catch {
        if (!cancelled) setBxStations([])
      }
    }

    loadStations()

    return () => {
      cancelled = true
    }
  }, [isBoostXpressOrdersView])

  useEffect(() => {
    let cancelled = false

    const fetchTotalOrders = async () => {
      try {
        // Fetch Regular Orders Total
        const regularOrdersPromise = orderService.index(1, 'DESC', 'ALL')

        // Fetch BoostExpress Orders Total
        const bxOrdersPromise = orderBXService.list({
          page: 1,
          limit: 1,
          sortOrder: 'DESC',
        })

        const [regularRes, bxRes] = await Promise.all([regularOrdersPromise, bxOrdersPromise])

        if (cancelled) return

        const regularTotal = regularRes?.data?.data?.total ?? 0
        const bxTotal = bxRes?.data?.meta?.pagination?.total ?? bxRes?.data?.data?.total ?? 0

        setRegularOrderCount(regularTotal)
        setBxOrderCount(bxTotal)
      } catch (error) {
        console.error('Failed to fetch total orders:', error)
      }
    }

    fetchTotalOrders()

    const fetchUserCounts = async () => {
      try {
        // Fetch TG Users Total
        const tgUsersPromise = customerService.index(1, 'DESC')

        // Fetch BoostExpress Users Total (from dashboard metrics)
        const tgRes = await dashboardBXService.getMetrics().then((res) => res.data.data)
        const bxMetrics = await tgUsersPromise.then((res) => res.data.data)

        if (cancelled) return

        const tgTotal = bxMetrics?.total ?? 0
        const bxTotal = tgRes.totalUsers ?? 0

        setTgUserCount(tgTotal)
        setBxUserCount(bxTotal)
      } catch (error) {
        console.error('Failed to fetch user counts:', error)
      }
    }

    fetchUserCounts()

    return () => {
      cancelled = true
    }
  }, [])

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalRecords / PAGE_SIZE))
  }, [totalRecords])

  const paginationItems = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1)
    if (pageNumber <= 3)
      return [1, 2, 3, 'ellipsis-start', totalPages - 2, totalPages - 1, totalPages]
    if (pageNumber >= totalPages - 2) {
      return [1, 2, 3, 'ellipsis-end', totalPages - 2, totalPages - 1, totalPages]
    }
    return [
      1,
      pageNumber - 1,
      pageNumber,
      pageNumber + 1,
      'ellipsis-mid',
      totalPages - 1,
      totalPages,
    ]
  }, [pageNumber, totalPages])

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages)
    }
  }, [pageNumber, totalPages])

  const handleExport = () => {
    const table = tableSectionRef.current?.querySelector('table')
    const headerCells = Array.from(table?.tHead?.rows[0]?.cells ?? [])
    const includedColumnIndexes = headerCells
      .map((cell, index) => ({ index, label: cell.textContent?.trim() ?? '' }))
      .filter(({ label }) => !/^actions?$/i.test(label))

    if (!table || !includedColumnIndexes.length) return

    const normalizeCellText = (cell?: HTMLTableCellElement) =>
      cell?.textContent?.replace(/\s+/g, ' ').trim() ?? ''

    const rows = Array.from(table.tBodies[0]?.rows ?? [])
      .filter((row) => !row.querySelector('.bx-finance__empty'))
      .map((row) =>
        includedColumnIndexes.map(({ index }) => normalizeCellText(row.cells[index])),
      )
      .filter((row) => row.some(Boolean))

    if (!rows.length) return

    const headers = includedColumnIndexes.map(({ label }) => label)
    const csv = Papa.unparse([headers, ...rows])
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${exportFileNames[resolvedSubTab]}-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  return (
    <div className='bx-finance mx-3 mx-lg-5'>
      <h2 className='bx-finance__title pt-4 mx-3 mx-lg-0'>Finance</h2>

      <div className='bx-finance__metrics row g-3'>
        {financeMetrics.map((metric: FinanceMetric) => (
          <div key={metric.label} className='col-12 col-md-6 col-xl-4 ps-0'>
            <div className={`bx-finance__metric-card bx-finance__metric-card--${metric.tone}`}>
              <div className='bx-finance__metric-label'>{metric.label}</div>
              <div className='bx-finance__metric-value'>{metric.value}</div>
              {/* <div className='bx-finance__metric-helper'>{metric.helper}</div> */}
            </div>
          </div>
        ))}
      </div>

      <div className='bx-finance__tab-shell mt-3'>
        <div className='bx-finance__primary-tabs'>
          <div className='bx-finance__primary-tabs-inner'>
            {categoryTabs.map((tab) => (
              <button
                key={tab.key}
                type='button'
                className={`bx-finance__primary-tab ${
                  activeCategory === tab.key ? 'bx-finance__primary-tab--active' : ''
                }`}
                onClick={() => {
                  setActiveCategory(tab.key)
                  setActiveSubTab(defaultSubTabs[tab.key])
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeCategory !== 'stockManagement' ? (
          <div className='bx-finance__secondary-tabs' ref={secondaryTabsRef}>
            <div className='bx-finance__secondary-tabs-inner'>
              {subTabLabels[activeCategory as Exclude<FinanceCategory, 'stockManagement'>].map(
                (tab) => (
                  <button
                    key={tab.key}
                    type='button'
                    className={`bx-finance__secondary-tab ${
                      resolvedSubTab === tab.key ? 'bx-finance__secondary-tab--active' : ''
                    }`}
                    ref={(el) => {
                      secondaryTabRefs.current[tab.key] = el
                    }}
                    onClick={() => setActiveSubTab(tab.key as FinanceSubTab)}
                  >
                    {tab.label}
                  </button>
                ),
              )}
            </div>
            <div
              className='bx-finance__secondary-indicator'
              style={{
                left: secondaryIndicator.left,
                width: secondaryIndicator.width ? Math.min(secondaryIndicator.width, 140) : 0,
              }}
            />
          </div>
        ) : null}

        <div className='bx-finance__toolbar row g-2 align-items-center'>
          <div className='col-12 col-md-10 col-xxl-10'>
            <div className='bx-search-wrapper w-100'>
              <div className='bx-search-icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                className='bx-search-input'
                placeholder={activeSearchPlaceholder}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPageNumber(1)
                }}
              />
            </div>
          </div>

          <div className='col-6 col-md-1 position-relative'>
            <button
              type='button'
              className='bx-action-btn w-100'
              onClick={() => setShowFilters((current) => !current)}
            >
              <img src={filterIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Filters</span>
            </button>

            {showFilters ? (
              <div className='bx-finance-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Records</h5>
                  <button
                    type='button'
                    className='bx-filters-close'
                    onClick={() => setShowFilters(false)}
                  >
                    ×
                  </button>
                </div>

                <div className='bx-filters-body'>
                  {activeCategory === 'boostxpress' && resolvedSubTab === 'userOrders' ? (
                    <>
                      <div className='bx-filter-group'>
                        <label>Station</label>
                        <select
                          className='form-control'
                          value={bxStationId ?? ''}
                          onChange={(e) => {
                            const value = e.target.value
                            setBxStationId(value ? value : undefined)
                            setPageNumber(1)
                          }}
                        >
                          <option value=''>All</option>
                          {bxStations.map((s) => (
                            <option value={s.id} key={s.id}>
                              {s.businessName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className='bx-filter-group'>
                        <label>Status</label>
                        <select
                          className='form-control'
                          value={bxOrderStatus}
                          onChange={(e) => {
                            setBxOrderStatus(e.target.value as OrderStatus)
                            setPageNumber(1)
                          }}
                        >
                          <option value=''>All</option>
                          <option value='CREATED'>CREATED</option>
                          <option value='IN_PROGRESS'>IN_PROGRESS</option>
                          <option value='COMPLETED'>COMPLETED</option>
                          <option value='CANCELLED'>CANCELLED</option>
                        </select>
                      </div>

                      <div className='bx-filter-group'>
                        <label>Payment Status</label>
                        <select
                          className='form-control'
                          value={bxPaymentStatus}
                          onChange={(e) => {
                            setBxPaymentStatus(e.target.value as PaymentStatus)
                            setPageNumber(1)
                          }}
                        >
                          <option value=''>All</option>
                          <option value='paid'>paid</option>
                          <option value='unpaid'>unpaid</option>
                          <option value='failed'>failed</option>
                          <option value='pending'>pending</option>
                          <option value='refunded'>refunded</option>
                        </select>
                      </div>

                      <div className='bx-filter-group'>
                        <label>Product</label>
                        <select
                          className='form-control'
                          value={bxProduct}
                          onChange={(e) => {
                            setBxProduct(e.target.value as OrderProduct)
                            setPageNumber(1)
                          }}
                        >
                          <option value=''>All</option>
                          <option value='PMS'>PMS</option>
                          <option value='AGO'>AGO</option>
                          <option value='LPG'>LPG</option>
                        </select>
                      </div>

                      <div className='bx-filter-group'>
                        <label>User Resource ID</label>
                        <input
                          type='text'
                          className='form-control'
                          value={bxUserResourceId}
                          onChange={(e) => {
                            setBxUserResourceId(e.target.value)
                            setPageNumber(1)
                          }}
                        />
                      </div>

                      <div className='bx-filter-group'>
                        <label>Start Date</label>
                        <input
                          type='date'
                          className='form-control'
                          value={bxStartDate}
                          onChange={(e) => {
                            setBxStartDate(e.target.value)
                            setPageNumber(1)
                          }}
                        />
                      </div>

                      <div className='bx-filter-group'>
                        <label>End Date</label>
                        <input
                          type='date'
                          className='form-control'
                          value={bxEndDate}
                          onChange={(e) => {
                            setBxEndDate(e.target.value)
                            setPageNumber(1)
                          }}
                        />
                      </div>
                    </>
                  ) : activeCategory === 'boostxpress' && resolvedSubTab === 'posTerminals' ? (
                    <>
                      <div className='bx-filter-group'>
                        <label>From</label>
                        <input
                          type='date'
                          className='form-control'
                          value={bxPosFrom ?? ''}
                          onChange={(e) => {
                            setBxPosFrom(e.target.value || undefined)
                            setPageNumber(1)
                          }}
                        />
                      </div>
                      <div className='bx-filter-group'>
                        <label>To</label>
                        <input
                          type='date'
                          className='form-control'
                          value={bxPosTo ?? ''}
                          onChange={(e) => {
                            setBxPosTo(e.target.value || undefined)
                            setPageNumber(1)
                          }}
                        />
                      </div>
                      <div className='bx-filter-group'>
                        <label>POS Terminal ID</label>
                        <input
                          type='text'
                          className='form-control'
                          value={bxPosTerminalId ?? ''}
                          onChange={(e) => {
                            setBxPosTerminalId(e.target.value || undefined)
                            setPageNumber(1)
                          }}
                        />
                      </div>
                      <div className='bx-filter-group'>
                        <label>Provider</label>
                        <select
                          className='form-control'
                          value={bxPosProvider ?? ''}
                          onChange={(e) => {
                            const value = e.target.value
                            setBxPosProvider(value ? value : undefined)
                            setPageNumber(1)
                          }}
                        >
                          <option value=''>All</option>
                          <option value='ALL'>ALL</option>
                          <option value='NOMBA'>NOMBA</option>
                          <option value='VFD'>VFD</option>
                        </select>
                      </div>
                    </>
                  ) : activeCategory === 'boostxpress' &&
                    resolvedSubTab === 'cashReconciliation' ? (
                    <div className='bx-filter-group'>
                      <label>Status</label>
                      <select
                        className='form-control'
                        value={bxCashReconStatus}
                        onChange={(e) => {
                          setBxCashReconStatus(e.target.value as ReportStatus | 'ALL' | '')
                          setPageNumber(1)
                        }}
                      >
                        <option value='ALL'>All reports</option>
                        <option value=''>Awaiting finance review</option>
                        <option value='PUBLISHED'>Published history</option>
                        <option value='FINANCE_CHANGES_REQUESTED'>Currently sent back</option>
                      </select>
                    </div>
                  ) : activeCategory === 'tdla' && resolvedSubTab === 'investments' ? (
                    <div className='bx-filter-group'>
                      <label>Status</label>
                      <select
                        className='form-control'
                        value={tdlaStatus}
                        onChange={(e) => {
                          setTdlaStatus(e.target.value as InvestorTokenAssetStatus)
                          setPageNumber(1)
                        }}
                      >
                        {tdlaStatusFilterOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : activeCategory === 'tdla' && resolvedSubTab === 'tradersCard' ? (
                    <div className='bx-filter-group'>
                      <label>Status</label>
                      <select
                        className='form-control'
                        value={tradersCardStatus}
                        onChange={(e) => {
                          setTradersCardStatus(e.target.value)
                          setPageNumber(1)
                        }}
                      >
                        {tradersCardStatusFilterOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className='bx-filter-group'>
                      <label>Status</label>
                      <select
                        className='form-control'
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value)
                          setPageNumber(1)
                        }}
                      >
                        {statusFilterOptionsByCategory[activeCategory].map((option) => (
                          <option key={option.value || 'all'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className='bx-filters-footer'>
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={() => {
                      setStatusFilter('')
                      setBxOrderStatus('')
                      setBxPaymentStatus('')
                      setBxProduct('')
                      setBxUserResourceId('')
                      setBxStationId(undefined)
                      setBxStartDate('')
                      setBxEndDate('')
                      setBxPosFrom(undefined)
                      setBxPosTo(undefined)
                      setBxPosTerminalId(undefined)
                      setBxPosProvider(undefined)
                      setBxCashReconStatus('')
                      setTdlaStatus('ACTIVE')
                      setTradersCardStatus('ALL')
                      setShowFilters(false)
                      setPageNumber(1)
                    }}
                  >
                    Clear Filters
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={() => setShowFilters(false)}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className='col-6 col-md-1'>
            <button type='button' className='bx-action-btn w-100' onClick={handleExport}>
              <img src={exportIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Export</span>
            </button>
          </div>
        </div>

        <div className='bx-finance-table-section' ref={tableSectionRef}>
          {isBoostXpressOrdersView && (
            <BoostXpressOrdersTable
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              searchTerm={searchTerm}
              status={bxOrderStatus}
              paymentStatus={bxPaymentStatus}
              product={bxProduct}
              userResourceId={bxUserResourceId}
              stationId={bxStationId}
              startDate={bxStartDate}
              endDate={bxEndDate}
              onTotalChange={setTotalRecords}
            />
          )}
          {isBoostXpressPosView && (
            <PosTerminalsTable
              pageNumber={pageNumber}
              pageSize={20}
              searchTerm={searchTerm}
              posFrom={bxPosFrom}
              posTo={bxPosTo}
              posTerminalId={bxPosTerminalId}
              posProvider={bxPosProvider}
              onTotalChange={setTotalRecords}
            />
          )}
          {isCashReconciliationView && (
            <CashReconciliationTable
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              searchTerm={searchTerm}
              status={bxCashReconStatus || undefined}
              onTotalChange={setTotalRecords}
            />
          )}
          {isStockManagementView && (
            <StockManagementTable
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              onTotalChange={setTotalRecords}
            />
          )}
          {isTdlaInvestmentsView && (
            <TdlaInvestmentsTable
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              status={tdlaStatus}
              onTotalChange={setTotalRecords}
            />
          )}
          {isTdlaTradersCardView && (
            <TradersCardTable
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              status={tradersCardStatus}
              onTotalChange={setTotalRecords}
            />
          )}
          {(isTgPlusView || isTransactionsView || resolvedSubTab === 'orders') && (
            <SubscriptionOrdersTable
              activeCategory={activeCategory}
              resolvedSubTab={resolvedSubTab}
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              onTotalChange={setTotalRecords}
            />
          )}
        </div>

        <div className='bx-pagination mb-5'>
          <button
            type='button'
            className='bx-pagination-btn'
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((current) => Math.max(1, current - 1))}
          >
            <span className='bx-pagination-icon-wrap'>
              <img src={arrowLeft} alt='' className='bx-pagination-icon bx-pagination-icon--left' />
            </span>
            <span className='bx-pagination-text'>Previous</span>
          </button>

          <div className='bx-pagination-numbers d-none d-md-flex'>
            {paginationItems.map((item, index) =>
              typeof item === 'number' ? (
                <button
                  key={item}
                  type='button'
                  className={`bx-page-number ${pageNumber === item ? 'bx-page-number--active' : ''}`}
                  onClick={() => setPageNumber(item)}
                >
                  {item}
                </button>
              ) : (
                <span key={`${item}-${index}`} className='bx-page-ellipsis'>
                  ...
                </span>
              ),
            )}
          </div>

          <button
            type='button'
            className='bx-pagination-btn'
            disabled={pageNumber >= totalPages}
            onClick={() => setPageNumber((current) => Math.min(totalPages, current + 1))}
          >
            <span className='bx-pagination-text'>Next</span>
            <span className='bx-pagination-icon-wrap'>
              <img src={arrowRight} alt='' className='bx-pagination-icon' />
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Finance
